import { type ReactNode } from 'react';
import { cn } from '@/nucleo/utilitarios';

export interface StructureNode {
  label: string;
  detail?: string;
  children?: StructureNode[];
}

/**
 * Diagrama visual de hierarquia — substitui árvores ASCII (que não têm
 * representação visual adequada no PDF).
 */
export function StructureDiagram({
  nodes,
  title,
  className,
}: {
  nodes: StructureNode[];
  title?: string;
  className?: string;
}) {
  return (
    <div
      className={cn('rounded-xl border border-border bg-card p-4 break-inside-avoid', className)}
    >
      {title && (
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          {title}
        </p>
      )}
      <div className="space-y-1.5">
        {nodes.map((n, i) => (
          <Node key={`${n.label}-${i}`} node={n} depth={0} />
        ))}
      </div>
    </div>
  );
}

function Node({ node, depth }: { node: StructureNode; depth: number }): ReactNode {
  return (
    <div style={{ marginLeft: depth * 16 }}>
      <div
        className={cn(
          'flex items-baseline gap-2 rounded-lg border px-3 py-1.5',
          depth === 0 ? 'border-primary/40 bg-primary/5' : 'border-border bg-muted/40',
        )}
      >
        <span className="text-xs font-mono font-semibold text-foreground">{node.label}</span>
        {node.detail && <span className="text-[11px] text-muted-foreground">{node.detail}</span>}
      </div>
      {node.children && (
        <div className="mt-1.5 space-y-1.5 border-l border-dashed border-border pl-2">
          {node.children.map((c, i) => (
            <Node key={`${c.label}-${i}`} node={c} depth={depth} />
          ))}
        </div>
      )}
    </div>
  );
}
